import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import toast from "react-hot-toast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useApi from "components/Utils/api";

// Sections mirror the HTML spec: Local / LKR / USD / DCC, four parties each.
const SECTIONS = [
  { key: "local", label: "Local cards" },
  { key: "lkr", label: "Foreign cards — LKR settlement" },
  { key: "usd", label: "Foreign cards — Foreign currency (USD)" },
  { key: "dcc", label: "Foreign cards — DCC transactions" },
];
const PARTIES = [
  { key: "Hnb", label: "HNB %" },
  { key: "Dms", label: "DMS %" },
  { key: "Yc", label: "YouCloud %" },
  { key: "Mf", label: "Merchant Final %" },
];

// Full list of the 16 rate field names, e.g. localHnb, lkrDms ...
const RATE_FIELDS = SECTIONS.flatMap((s) => PARTIES.map((p) => `${s.key}${p.key}`));

const emptyRates = () => RATE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: "" }), {});

function MdrPosModal({ open, onClose, editRow, availableSegments, getMasters }) {
  const api = useApi();
  const isEdit = Boolean(editRow);
  const [mccCode, setMccCode] = useState("");
  const [rates, setRates] = useState(emptyRates());

  useEffect(() => {
    if (!open) return;
    if (editRow) {
      setMccCode(editRow.mccCode || "");
      setRates(
        RATE_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f]: editRow[f] === null || editRow[f] === undefined ? "" : editRow[f] }),
          {}
        )
      );
    } else {
      setMccCode("");
      setRates(emptyRates());
    }
  }, [open, editRow]);

  const buildBody = () => {
    const body = {};
    RATE_FIELDS.forEach((f) => {
      body[f] = rates[f] === "" ? null : parseFloat(rates[f]);
    });
    return body;
  };

  const validate = () => {
    if (!isEdit && !mccCode) {
      toast.error("Please select a segment");
      return false;
    }
    for (let i = 0; i < RATE_FIELDS.length; i += 1) {
      const f = RATE_FIELDS[i];
      if (rates[f] === "" || isNaN(parseFloat(rates[f]))) {
        toast.error("Please enter all rate values");
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;

    if (isEdit) {
      api
        .put(`master/pos/${editRow.mcId}`, buildBody())
        .then((response) => {
          if (response.data && response.data.responseCode === 200) {
            toast.success("POS MDR updated for " + (editRow.segmentName || editRow.mccCode));
            onClose();
            getMasters();
          } else {
            toast.error((response.data && response.data.responseMessage) || "Failed to update POS MDR");
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            toast.error("You do not have permission to perform this action.");
            return;
          }
          toast.error("Failed to update POS MDR");
        });
    } else {
      api
        .post("master/pos/create", { mccCode, ...buildBody() })
        .then((response) => {
          if (response.data && response.data.responseCode === 200) {
            toast.success("POS MDR added");
            onClose();
            getMasters();
          } else {
            toast.error((response.data && response.data.responseMessage) || "Failed to add POS MDR");
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            toast.error("You do not have permission to perform this action.");
            return;
          }
          if (error.response && error.response.status === 406) {
            toast.error((error.response.data && error.response.data.responseMessage) || "Segment already configured");
            return;
          }
          toast.error("Failed to add POS MDR");
        });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? `Edit Segment MDR — ${editRow.segmentName || editRow.mccCode}` : "Add Segment MDR"} (POS)</DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {isEdit ? (
                <TextField
                  fullWidth
                  label="Merchant Segment"
                  value={`${editRow.mccCode} - ${editRow.segmentName || ""}`}
                  disabled
                />
              ) : (
                <TextField
                  select
                  fullWidth
                  label="Merchant Segment"
                  value={mccCode}
                  onChange={(e) => setMccCode(e.target.value)}
                >
                  <MenuItem value="">— Select segment —</MenuItem>
                  {availableSegments.map((seg) => (
                    <MenuItem key={seg.mccCode} value={seg.mccCode}>
                      {seg.mccCode} - {seg.description}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          </Grid>

          {SECTIONS.map((section) => (
            <MDBox key={section.key} mt={3}>
              <MDTypography variant="button" fontWeight="bold" textTransform="uppercase">
                {section.key} — {section.label}
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                {PARTIES.map((party) => {
                  const field = `${section.key}${party.key}`;
                  return (
                    <Grid item xs={6} md={3} key={field}>
                      <TextField
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.001" }}
                        label={party.label}
                        value={rates[field]}
                        onChange={(e) => setRates({ ...rates, [field]: e.target.value })}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </MDBox>
          ))}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton color="info" variant="gradient" onClick={handleSave}>
          {isEdit ? "Update" : "Add"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

MdrPosModal.defaultProps = {
  open: false,
  onClose: () => {},
  editRow: null,
  availableSegments: [],
  getMasters: () => {},
};

MdrPosModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  editRow: PropTypes.object,
  availableSegments: PropTypes.array,
  getMasters: PropTypes.func,
};

export default MdrPosModal;
